import {COMPANY_KEYS, useInviteCompanyMemberMutation} from "@/api/companyApi";
import Modal from "@/components/Common/Modal";
import PermissionSelect from "@/components/MultiAccount/PermissionSelect";
import type {SubBrandInviteSerializer} from "@/generated/Company";
import {client} from "@/httpClient";
import AvatarPlaceholder from "@/images/navbar_logged_out_profile_icon.svg";
import {queryClient} from "@/queryClient";
import {PERMISSIONS} from "@/store/constants";
import type {TPermission} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

const RESEND = "RESEND";
const REMOVE = "REMOVE";

const UpdateInvitePermission: FC<{
  companyId: number;
  invite: SubBrandInviteSerializer;
  permission: TPermission;
  isCompanyEdit?: boolean;
}> = ({companyId, invite, permission, isCompanyEdit = false}) => {
  const {t} = useTranslation("multi_account");
  const [selectedPermission, setSelectedPermission] = useState<TPermission>(permission);
  const [showRemoveInvitationModal, setShowRemoveInvitationModal] = useState(false);

  const {mutate: inviteMember} = useInviteCompanyMemberMutation({
    companyId,
    email: invite.email,
    selectedPermission: PERMISSIONS.includes(selectedPermission) ? selectedPermission : permission,
    onSuccessCallback: () => {
      toast.success(t(permission === selectedPermission ? "resend_invitation_success" : "permission_update_success"));
    },
  });
  const {mutate: removeInvitation} = useMutation({
    mutationKey: COMPANY_KEYS.mCompanyMemberInviteRemoval(companyId),
    mutationFn: () => client.post(COMPANY_KEYS.mCompanyMemberInviteRemoval(companyId)[0], {email: invite.email}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyInvites(companyId)});
    },
  });

  return (
    <>
      {showRemoveInvitationModal && (
        <Modal
          backgroundClassName="max-w-[600px]"
          modalTitle={t("remove_invitation")}
          onClose={() => setShowRemoveInvitationModal(false)}
          onConfirm={() => removeInvitation()}
          confirmButtonText={t("remove_invitation")}>
          <div className="flex flex-col items-center gap-5">
            <span className="text-neutral-black text-center">{t("remove_invitation_confirmation")}</span>
            <div className="border-neutral-grey-light-dark flex items-center gap-2 rounded-full border p-1 pr-2">
              <div className="size-6 shrink-0 rounded-full">
                <img src={AvatarPlaceholder} className="h-full w-full rounded-full object-fill" />
              </div>
              <span className="bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
                {invite?.email}
              </span>
            </div>
          </div>
        </Modal>
      )}
      <PermissionSelect
        value={permission}
        onChange={async value => {
          if (value === REMOVE) {
            setShowRemoveInvitationModal(true);
            return;
          }

          if (value === RESEND) {
            inviteMember();
            return;
          }

          if (value !== permission) {
            setSelectedPermission(value as TPermission);
            inviteMember();
          }
        }}
        containerClassName="max-w-[185px]"
        bottomOptions={
          isCompanyEdit
            ? [
                {
                  label: t("resend_invitation"),
                  value: RESEND,
                },
                {
                  label: t("remove_invitation"),
                  value: REMOVE,
                },
              ]
            : []
        }
      />
    </>
  );
};

export default UpdateInvitePermission;
